import React from "react";
import "../styles/service.css";
import Navbar from "../components/layout/Navbar";
import SeoHead from "../components/SeoHead";

const CONDITIONS = [
  {
    title: "Congenital Deformities",
    desc: "Corrective treatment for hand conditions present from birth.",
  },
  {
    title: "Hand Injuries & Fractures",
    desc: "Expert care for traumatic injuries, fractures, and dislocations.",
  },
  {
    title: "Industrial & Crush Injuries",
    desc: "Specialized reconstruction for workplace and crush trauma.",
  },
  {
    title: "Replantation Surgery",
    desc: "Reimplantation of amputated fingers and hands.",
  },
  {
    title: "Burns & Post-Burn Deformities",
    desc: "Treatment of hand burns and reconstruction of post-burn scars.",
  },
  {
    title: "Tendon & Nerve Injuries",
    desc: "Microsurgical repair of damaged tendons and peripheral nerves.",
  },
  {
    title: "Vascular Injuries",
    desc: "Upper limb vascular injury repair and malformation correction.",
  },
  {
    title: "Spastic & Paralytic Hand",
    desc: "Deformity correction for spastic and paralytic hand conditions.",
  },
  {
    title: "Rheumatoid Deformities",
    desc: "Surgical management of hand deformities from rheumatoid arthritis.",
  },
  {
    title: "Dupuytren's Contracture",
    desc: "Treatment for progressive hand contracture conditions.",
  },
  {
    title: "Joint Arthritis",
    desc: "Management of CMC basal joint arthritis and ligament injuries.",
  },
  {
    title: "Hand Tumours",
    desc: "Diagnosis and surgical removal of benign and malignant tumours.",
  },
];

const GALLERY = [1, 2, 3, 4, 5,6,7,8,9,10,11,12,13].map((n) => ({
  src: `/images/hand${n}.jpeg`,
  alt: `Hand surgery case ${n}`,
}));

export default function HandSurgery() {
  return (
    <div className="hs-page">
      <SeoHead
        title="Hand Surgery in Hyderabad | Dr. R. Suneel"
        description="Specialist hand surgery for fractures, tendon and nerve injuries, replantation and reconstruction. Book a consultation with Dr. R. Suneel in Hyderabad."
        keywords="hand surgery Hyderabad, hand surgeon Hyderabad, best hand surgery doctor Hyderabad, hand injury specialist Hyderabad, hand fracture surgery Hyderabad, reconstructive hand surgery, microsurgery for hand injuries, tendon repair surgery Hyderabad, finger injury treatment Hyderabad, hand trauma surgeon, Dr R Suneel hand surgeon, Medicover hand surgery Hyderabad"
        url="https://www.handandwristclinics.com/hand"
        image="https://www.handandwristclinics.com/static/media/hand_surgery.png"
      />

      {/* HERO + BREADCRUMB */}
      <header className="hs-hero">
        <div className="hs-hero-inner">
          <nav className="hs-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a>
            <span className="hs-bc-sep">/</span>
            <a href="/services">Services</a>
            <span className="hs-bc-sep">/</span>
            <span className="hs-bc-current">Hand Surgery</span>
          </nav>
          <div className="hs-eyebrow">
            <span></span> Specialized Hand & Wrist Care
          </div>
          <h1 className="hs-title">
            Hand <em>Surgery</em>
          </h1>
          <div className="hs-divider" />
          <p className="hs-hero-sub">
            Advanced surgical treatment to restore function, strength, and mobility
            to injured or impaired hands.
          </p>
        </div>
      </header>

      {/* INTRO */}
      <section className="hs-section hs-intro">
        <div className="hs-container">
          <div className="hs-section-tag">About the Procedure</div>
          <h2 className="hs-section-title">
            Restoring the intricate <em>function of your hands</em>
          </h2>
          <div className="hs-divider" />
          <p className="hs-intro-text">
            The hand is one of the most intricate and delicate organs in the human
            body. Hand surgery is a broad discipline covering a wide range of
            procedures designed to address impairment, disease, or injury. At our
            Center for Hand Surgery in Hyderabad, our specialists focus on restoring
            the normal function of fingers and hands affected by trauma or
            congenital abnormalities.
          </p>
          <p className="hs-intro-text">
            Our leading hand injury clinic in Madhapur offers comprehensive
            reconstructive surgery for dislocations, fractures, and complex
            deformities — helping patients regain strength, feeling, and full range
            of motion.
          </p>
        </div>
      </section>

      {/* CONDITIONS GRID */}
      <section className="hs-section hs-conditions">
        <div className="hs-container">
          <div className="hs-section-head">
            <div className="hs-section-tag">Conditions We Treat</div>
            <h2 className="hs-section-title">
              Comprehensive <em>hand & wrist</em> treatment
            </h2>
            <div className="hs-divider" />
            <p className="hs-section-sub">
              From traumatic injuries to congenital conditions, we offer surgical
              expertise across the full spectrum of hand disorders.
            </p>
          </div>

          <div className="hs-grid">
            {CONDITIONS.map((c, i) => (
              <article className="hs-card" key={i}>
                <div className="hs-card-num">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="hs-card-title">{c.title}</h3>
                <p className="hs-card-desc">{c.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SMALL TEXT / CLOSING NOTE */}
      <section className="hs-section hs-note">
        <div className="hs-container hs-note-inner">
          <p className="hs-note-text">
            Every hand is unique — and so is every treatment plan. Our team
            combines microsurgical precision with years of specialist experience to
            deliver personalized care tailored to your recovery goals.
          </p>
        </div>
      </section>

      {/* IMAGE GALLERY */}
      <section className="hs-section hs-gallery-section">
        <div className="hs-container">
          <div className="hs-section-head hs-center">
            <div className="hs-section-tag">Case Gallery</div>
            <h2 className="hs-section-title">
              Results from our <em>hand surgery</em> cases
            </h2>
            <div className="hs-divider hs-center-divider" />
          </div>

          <div className="hs-gallery">
            {GALLERY.map((img, i) => (
              <figure className="hs-gallery-item" key={i}>
                <img src={img.src} alt={img.alt} loading="lazy" />
                <figcaption className="hs-gallery-cap">
                  Case {String(i + 1).padStart(2, "0")}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      
    </div>
  );
}